import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import "../FreeAssesment/loader.css";
import logo from "../../assets/ExamWarriorLogo.svg";
import { FaChevronLeft } from "react-icons/fa";

function WritingTestPage() {
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [timer, setTimer] = useState(30 * 60); 
    const [answers, setAnswers] = useState({ writing: "" });
    const [isLoading, setIsLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [showCharacterDescription, setShowCharacterDescription] = useState(true);

    const navigate = useNavigate();
    const location = useLocation();

    const user = JSON.parse(localStorage.getItem('user'));
    const childId = user?.id || "";
    
    // Determine student type
    const studentType = user?.is_selective ? 'selective' : user?.is_oc ? 'oc' : 'selective';
    
    const [testTypeInfo, setTestTypeInfo] = useState({
        isPackageTest: false,
        isIndividualTest: false,
        testId: null,
        testNumber: 1
    });

    useEffect(() => {
        window.history.pushState(null, null, window.location.pathname);
        const preventBackNavigation = () => {
            window.history.pushState(null, null, window.location.pathname);
            alert("Back navigation is disabled during the test.");
        };

        window.addEventListener("popstate", preventBackNavigation);
        return () => window.removeEventListener("popstate", preventBackNavigation);
    }, []);

    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        handleSubmit();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [timer]);

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                const { testId, packageTest, individualTest, testNumber } = location.state || {};
                
                let url = `${process.env.REACT_APP_BACKEND_URL}/api/mock-test/writing`;
                const params = new URLSearchParams();

                if (packageTest && testId) {
                    params.append('packageTest', 'true');
                    params.append('testId', testId);
                    
                    // Extract test number from testId if not provided
                    if (testNumber) {
                        params.append('testNumber', testNumber);
                    } else {
                        const match = testId.match(/mock-package-(\d+)/);
                        if (match) {
                            params.append('testNumber', match[1]);
                        }
                    }
                }
                
                if (individualTest && testNumber) {
                    params.append('individualTest', 'true');
                    params.append('testNumber', testNumber);
                }
                
                params.append('studentType', studentType);

                if (params.toString()) {
                    url += `?${params.toString()}`;
                }

                console.log('📡 Fetching writing questions from:', url);

                const response = await axios.get(url);

                if (!response.data || !Array.isArray(response.data.questions)) {
                    throw new Error("Invalid API response. Questions not found.");
                }

                setQuestions(response.data.questions);
                
                // Store test type info
                let extractedTestNumber = testNumber || 1;
                if (!extractedTestNumber && packageTest && testId) {
                    const match = testId.match(/mock-package-(\d+)/);
                    if (match) {
                        extractedTestNumber = parseInt(match[1]);
                    }
                }

                setTestTypeInfo({
                    isPackageTest: packageTest || false,
                    isIndividualTest: individualTest || false,
                    testId: testId || null,
                    testNumber: extractedTestNumber
                });
                
                setLoading(false);
            } catch (error) {
                console.error("Error fetching writing questions:", error);
                setLoading(false);
                toast.error("Failed to load writing task. Please try again.");
            }
        };

        fetchQuestions();
    }, [location.state, studentType]);

    const handleWritingAnswer = (event) => {
        const inputText = event.target.value;
        const wordCount = inputText.trim().split(/\s+/).length;

        if (wordCount > 300) {
            toast.error("Your response is too long. Please limit to 300 words.");
            const words = inputText.trim().split(/\s+/).slice(0, 300).join(' ');
            setAnswers({ writing: words });
            return;
        }
        setAnswers({ writing: inputText });
    };

    const handleButtonClick = () => {
        setShowPopup(true);
    };

    const handleCancel = () => {
        setShowPopup(false);
    };

    const handleConfirm = () => {
        setShowPopup(false);
        handleSubmit();
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        if (!questions.length) {
            toast.error("No writing task found.");
            return;
        }

        const questionStatus = [{
            questionNumber: 1,
            status: answers.writing.trim() ? "attempted" : "unattempted",
        }];

        const payload = {
            childId,
            subject: "writing",
            testType: "mock",
            questionStatus,
            responses: { writing: answers.writing },
            packageTest: testTypeInfo.isPackageTest,
            individualTest: testTypeInfo.isIndividualTest,
            testId: testTypeInfo.testId,
            testNumber: testTypeInfo.testNumber
        };

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/mock-test/submit-writing`,
                payload
            );

            toast.success("Writing test submitted for review! It will be graded by a teacher.");

            if (testTypeInfo.isIndividualTest) {
                setTimeout(() => navigate("/individual-mock-tests", { 
                    state: { subject: "Writing" } 
                }), 2000);
            } else {
                setTimeout(() => navigate("/mock-tests"), 2000);
            }
        } catch (error) {
            toast.error("Error submitting writing test.");
            console.error("Submission Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStartWriting = () => {
        setShowCharacterDescription(false);
    };

    const handleBackToDescription = () => {
        setShowCharacterDescription(true);
    };

    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center">
                <div className="loadingspinner">
                    <div id="square1"></div>
                    <div id="square2"></div>
                    <div id="square3"></div>
                    <div id="square4"></div>
                    <div id="square5"></div>
                </div>
            </div>
        );
    }

    if (!questions.length) {
        return (
            <div className="h-screen flex items-center justify-center">
                <p>No writing task found. Please try again later.</p>
            </div>
        );
    }

    const currentQuestion = questions[0];
    const minutes = Math.floor(timer / 60);
    const seconds = String(timer % 60).padStart(2, "0");
    const wordCount = answers.writing ? answers.writing.trim().split(/\s+/).length : 0;

    return (
        <div className="h-screen bg-white flex flex-col">
            {/* Header with Timer */}
            <div className="absolute w-full h-20 md:h-24 flex justify-between items-center py-1 px-3 md:px-6 bg-white shadow-md z-10">
                <img src={logo} alt="img-logo" className="h-16 w-24 md:h-24 md:w-32" />
                <div className="flex justify-center items-center bg-black text-white text-lg md:text-xl font-bold p-1 shadow-lg">
                    <div className="flex flex-col items-center mx-1 md:mx-2">
                        <span>{minutes}</span>
                        <span className="text-xs md:text-sm text-gray-400 uppercase tracking-wide">
                            Min
                        </span>
                    </div>
                    <span className="text-2xl md:text-4xl mx-1 md:mx-2">:</span>
                    <div className="flex flex-col items-center mx-1 md:mx-2">
                        <span>{seconds}</span>
                        <span className="text-xs md:text-sm text-gray-400 uppercase tracking-wide">
                            Sec
                        </span>
                    </div>
                </div>
                {/* Show test number */}
                <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                    Test #{testTypeInfo.testNumber}
                </div>
            </div>

            <div className="flex-grow flex flex-col pt-20 overflow-hidden">
                {showCharacterDescription ? (
                    /* Character Description Page */
                    <div className="w-full bg-white p-6 overflow-y-auto max-h-[calc(100vh-120px)]">
                        <div className="max-w-4xl mx-auto">
                            <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                                Character Description Task - Test #{testTypeInfo.testNumber}
                            </h1>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                                <h2 className="text-xl font-bold text-blue-800 mb-4">
                                    Writing Question:
                                </h2>
                                <div className="bg-white p-4 rounded-lg border border-blue-300">
                                    <p className="text-lg text-gray-800 leading-relaxed">
                                        {currentQuestion?.question || "No question available"}
                                    </p>
                                </div>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                                <h2 className="text-xl font-bold text-blue-800 mb-4">
                                    Your Task:
                                </h2>

                                <div className="space-y-4 text-gray-700">
                                    <p>
                                        <strong>Write a narrative that contains a full DESCRIPTION of your MAIN CHARACTER.</strong>
                                    </p>
                                    <p>
                                        <strong>Your narrative should contain clear details of the character's <em>physical appearance</em>.</strong>
                                    </p>
                                    <p>
                                        You will also need to reveal the character's <em>personality</em> through their <em>actions</em> and the way they <em>interact</em> with other characters in the narrative.
                                    </p>
                                    <p>
                                        <strong>REMEMBER:</strong> you must still write an interesting narrative with an engaging <em>plot</em>, <em>climax</em> and <em>resolution</em>.
                                    </p>
                                </div>

                                <div className="mt-6 bg-white p-4 rounded border">
                                    <h3 className="font-bold text-gray-800 mb-2">Before you begin writing:</h3>
                                    <p className="text-gray-700">
                                        Think carefully about what you need to say and how to structure and organise ideas using sentences and paragraphs.
                                    </p>
                                </div>

                                <div className="mt-4 bg-white p-4 rounded border">
                                    <h3 className="font-bold text-gray-800 mb-2">Your writing will be judged on:</h3>
                                    <p className="text-gray-700">
                                        your creativity and your ability to write effectively for a purpose and audience. Grammar, punctuation, spelling and vocabulary are important.
                                    </p>
                                </div>
                            </div>

                            {/* Start Writing Button */}
                            <div className="text-center mt-8">
                                <button
                                    onClick={handleStartWriting}
                                    className="start-test-button"
                                >
                                    START WRITING
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    /* Writing Page with Text Box */
                    <div className="w-full bg-white p-6 overflow-y-auto max-h-[calc(100vh-120px)]">
                        <div className="max-w-4xl mx-auto">
                            <div className="mb-6">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                    <h3 className="text-lg font-bold text-blue-800 mb-2">Writing Question (Test #{testTypeInfo.testNumber}):</h3>
                                    <p className="text-gray-800">
                                        {currentQuestion?.question || "Write your narrative below:"}
                                    </p>
                                </div>

                                <h2 className="text-xl font-bold text-gray-800 mb-4">
                                    Your Answer:
                                </h2>
                            </div>

                            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 mb-4">
                                <textarea
                                    className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Write your narrative here... Remember to include full character description, physical appearance, personality through actions, and an engaging plot with climax and resolution."
                                    value={answers.writing}
                                    onChange={handleWritingAnswer}
                                />
                                <div className="flex justify-between items-center mt-2">
                                    <p className="text-sm text-gray-600">
                                        Word Count: <span className={wordCount > 300 ? 'text-red-600 font-bold' : 'text-gray-800'}>
                                            {wordCount}
                                        </span> / 300 words
                                    </p>
                                    {wordCount > 300 && (
                                        <p className="text-sm text-red-600 font-bold">
                                            Exceeds word limit! Please reduce your response.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer Navigation */}
            <div className="relative bottom-0 left-0 right-0 bg-teal-500 text-white py-3 flex justify-between z-30">
                {!showCharacterDescription && (
                    <>
                        <button
                            onClick={handleBackToDescription}
                            className="p-2 px-4 rounded-md font-semibold bg-black ml-2"
                        >
                            &lt;&lt; BACK TO DESCRIPTION
                        </button>

                        <button
                            onClick={handleButtonClick}
                            className="bg-black text-white font-bold p-2 px-6 mr-3 rounded-md shadow-lg hover:scale-105 transform transition-all duration-300 ease-in-out"
                            disabled={wordCount > 300}
                        >
                            Submit Writing!
                        </button>
                    </>
                )}
            </div>

            {/* Confirmation Popup */}
            {showPopup && (
                <div className="fixed z-50 inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="relative bg-white rounded-lg shadow-lg overflow-hidden transform transition-all w-full max-w-md sm:mx-auto">
                        <button
                            onClick={handleCancel}
                            type="button"
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                            <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="px-6 py-8">
                            <div className="flex items-center space-x-4">
                                <div className="flex-shrink-0">
                                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                                        <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Confirm Submission
                                </h3>
                            </div>

                            <div className="mt-4 text-gray-700">
                                Are you sure you want to submit your writing? Once submitted, you cannot make changes.
                            </div>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    onClick={handleCancel}
                                    type="button"
                                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    type="button"
                                    className="px-4 py-2 text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Confirm Submission
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isLoading && (
                <div className="fixed inset-0 flex justify-center items-center bg-gray-500 bg-opacity-50 z-50">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
                    <span className="ml-4 text-white">
                        Submitting your writing for review...
                    </span>
                </div>
            )}

            <ToastContainer />
        </div>
    );
}

export default WritingTestPage;